import { createServerFn } from '@tanstack/react-start'
import products from '#bv/data/products'
import { getHubChildProductIds, isProductFamilyHubId } from '#bv/data/productFamilies'
import { getServiceSupabase } from '#bv/lib/supabaseAdminServer'

export type ProductStockMap = Record<number, boolean>

function defaultInStock(productId: number): boolean {
  const product = products.find((p) => p.id === productId)
  // Admin-created SKUs use synthetic IDs not present in the static base catalog; they are
  // in stock by default (merged catalog sets stock) unless `product_stock_overrides` says otherwise.
  if (!product) return true
  return product.stock > 0
}

function normalizeStockRows(rows: Array<{ product_id: number; in_stock: boolean }>): ProductStockMap {
  const map: ProductStockMap = {}
  for (const row of rows) {
    if (Number.isInteger(row.product_id)) {
      map[row.product_id] = !!row.in_stock
    }
  }
  return map
}

async function readStockOverrides(): Promise<ProductStockMap> {
  const sb = getServiceSupabase()
  if (!sb) return {}
  const { data, error } = await sb
    .from('product_stock_overrides')
    .select('product_id,in_stock')
  if (error || !Array.isArray(data)) return {}
  return normalizeStockRows(data as Array<{ product_id: number; in_stock: boolean }>)
}

function isSingleProductInStock(productId: number, map: ProductStockMap): boolean {
  if (Object.prototype.hasOwnProperty.call(map, productId)) {
    return !!map[productId]
  }
  return defaultInStock(productId)
}

/** Hub cards are in stock when any tier child is in stock. */
export function isProductInStock(productId: number, map: ProductStockMap): boolean {
  if (isProductFamilyHubId(productId)) {
    const childIds = getHubChildProductIds(productId)
    if (childIds?.length) {
      return childIds.some((id) => isSingleProductInStock(id, map))
    }
  }
  return isSingleProductInStock(productId, map)
}

export const getProductStockMap = createServerFn({ method: 'GET' }).handler(async () => {
  return readStockOverrides()
})

export async function assertProductsInStockOrThrow(items: Array<{ productId: number; name?: string }>) {
  const map = await readStockOverrides()
  const blocked = items.filter((item) => !isProductInStock(item.productId, map))
  if (blocked.length === 0) return
  const names = blocked.map((b) => b.name?.trim() || `Product #${b.productId}`)
  throw new Error(`Out of stock: ${names.join(', ')}`)
}

